import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

const treatments = [
  {
    id: "parodontologie",
    title: "Parodontologie",
    description:
      "Prévention et traitement des maladies des gencives et des tissus de soutien, pour préserver la santé de vos dents sur le long terme.",
    image: "/treatments/parodontologie.png",
    alt: "Examen des gencives — parodontologie",
    details: {
      kind: "items",
      items: [
        {
          id: "surfacage",
          title: "Surfaçage radiculaire",
          description:
            "Nettoyage approfondi de la surface des racines dentaires sous la gencive, pour éliminer plaque et tartre inaccessibles au brossage. Ce traitement stoppe la progression de la maladie parodontale et favorise la cicatrisation des tissus de soutien.",
          image: "/treatments/surfacage.png",
        },
        {
          id: "bilan-parodontal",
          title: "Bilan parodontal",
          description:
            "Examen complet des gencives et des tissus de soutien. Nous mesurons les poches parodontales, évaluons la perte osseuse et établissons un plan de traitement personnalisé adapté à votre situation.",
          image: "/treatments/bilan-parodontal.png",
        },
      ],
    },
  },
  {
    id: "prothese",
    title: "Prothèse",
    description:
      "Restauration fonctionnelle et esthétique : couronnes, bridges et prothèses sur-mesure réalisés avec les matériaux les plus modernes.",
    image: "/treatments/prothese.png",
    alt: "Prothèse dentaire couronne céramique",
    details: {
      kind: "items",
      items: [
        {
          id: "couronne",
          title: "Couronne unitaire",
          description:
            "Prothèse fixe réalisée sur-mesure en céramique haute qualité, posée sur une dent abîmée ou dévitalisée. Elle restaure la forme, la fonction et l'esthétique naturelle de la dent avec un rendu parfaitement intégré à votre sourire.",
          image: "/treatments/couronne.png",
        },
        {
          id: "bridge",
          title: "Bridge — 1 ou plusieurs éléments",
          description:
            "Prothèse fixe qui remplace une ou plusieurs dents manquantes en prenant appui sur les dents voisines. Solution durable et esthétique pour retrouver une mastication confortable et un sourire harmonieux.",
          image: "/treatments/bridge.png",
        },
        {
          id: "onlay-overlay",
          title: "Onlay & Overlay",
          description:
            "Restaurations indirectes en céramique réalisées sur-mesure en laboratoire. Alternatives esthétiques aux grandes obturations classiques, elles préservent davantage de tissu dentaire sain tout en offrant résistance et longévité supérieures.",
          image: "/treatments/onlay-overlay.png",
        },
      ],
    },
  },
  {
    id: "prothese-implant",
    title: "Prothèse sur implant",
    description:
      "Solutions prothétiques fixes ou amovibles qui prennent appui sur des implants dentaires, pour remplacer une, plusieurs ou toutes les dents avec stabilité et confort.",
    image: "/treatments/prothese-implant.png",
    alt: "Prothèse dentaire sur implant titane",
    details: {
      kind: "items",
      items: [
        {
          id: "couronne-implant",
          title: "Couronne sur implant unitaire",
          description:
            "Remplacement d'une dent manquante par une couronne céramique fixée sur un implant titane. Solution esthétique et définitive qui préserve les dents voisines, sans avoir à les tailler.",
          image: "/treatments/couronne-implant.png",
        },
        {
          id: "bridge-implant",
          title: "Bridge sur implant",
          description:
            "Prothèse fixe qui remplace plusieurs dents manquantes en prenant appui sur deux implants ou plus. Idéal lorsque plusieurs dents adjacentes sont à remplacer, sans solliciter les dents saines voisines.",
          image: "/treatments/bridge-implant.png",
        },
        {
          id: "prothese-amovible-implant",
          title: "Prothèse amovible complète sur implant",
          description:
            "Prothèse totale stabilisée par plusieurs implants grâce à un système de clips ou de barre. Elle reste amovible pour l'entretien quotidien, tout en offrant une tenue bien supérieure à un dentier classique.",
          image: "/treatments/prothese-amovible-implant.png",
        },
        {
          id: "prothese-complete-implant",
          title: "Prothèse complète sur implant",
          description:
            "Bridge fixe complet vissé sur plusieurs implants pour restaurer toute une arcade. Une solution définitive qui redonne une mastication, une esthétique et un confort proches d'une dentition naturelle.",
          image: "/treatments/prothese-complete-implant.png",
        },
      ],
    },
  },
  {
    id: "facettes",
    title: "Esthétique — Facettes",
    description:
      "Des facettes céramiques ultra-fines pour corriger forme, teinte et alignement. Un sourire transformé, naturel et lumineux.",
    image: "/treatments/facettes.png",
    alt: "Sourire lumineux avec facettes dentaires",
    details: {
      kind: "text",
      body: [
        "Les facettes dentaires sont de fines pellicules en céramique, de l'épaisseur d'une lentille de contact, collées sur la face visible des dents. Elles permettent de corriger en profondeur la forme, la teinte et l'alignement du sourire, avec un rendu ultra-naturel.",
        "Elles sont réalisées entièrement sur-mesure en laboratoire, dans une céramique haute qualité qui imite parfaitement l'émail naturel.",
      ],
      indications: [
        "Dents tachées ou décolorées résistantes à l'éclaircissement",
        "Dents ébréchées ou abîmées",
        "Petites malpositions non corrigées",
        "Espaces inter-dentaires (diastèmes)",
        "Usure du bord incisif",
      ],
    },
  },
  {
    id: "eclaircissement",
    title: "Éclaircissement",
    description:
      "Un sourire plus éclatant en toute sécurité, grâce à nos protocoles d'éclaircissement professionnels adaptés à votre dentition.",
    image: "/treatments/eclaircissement.png",
    alt: "Éclaircissement dentaire professionnel",
    details: {
      kind: "text",
      body: [
        "L'éclaircissement dentaire est une technique professionnelle qui permet d'éclaircir naturellement la teinte de vos dents grâce à un gel à base de peroxyde, toujours sous contrôle médical.",
        "Plusieurs protocoles existent — au fauteuil en cabinet, ou en ambulatoire avec gouttières sur-mesure — adaptés à votre sensibilité dentaire et au résultat souhaité. Sûr et progressif, il préserve l'émail tout en redonnant éclat et luminosité à votre sourire.",
      ],
      indications: [
        "Dents jaunies par le temps ou le vieillissement",
        "Dents tachées par le café, le thé, le vin ou le tabac",
        "Dents grisâtres ou ternes",
        "Envie d'un sourire plus éclatant avant un événement",
      ],
    },
  },
];

const TreatmentCard = ({ t, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.7,
        delay: idx * 0.1,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="card-premium group flex flex-col overflow-hidden"
      data-testid={`treatment-${t.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={t.image}
          alt={t.alt}
          className="card-image absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D241E]/40 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-6 right-6">
          <h3 className="font-serif text-3xl text-white leading-tight drop-shadow-md">
            {t.title}
          </h3>
        </div>
      </div>
      <div className="p-7 md:p-8 flex flex-col flex-1">
        <p className="body-text text-[15px] leading-relaxed">
          {t.description}
        </p>

        <button
          onClick={() => setOpen((v) => !v)}
          className="mt-6 self-start inline-flex items-center gap-2 text-[13px] uppercase tracking-widest font-semibold text-[#BE6C42] hover:text-[#A65A34] transition-colors"
          data-testid={`toggle-treatment-${t.id}`}
        >
          {open ? "Voir moins" : "Voir plus"}
          <ChevronDown
            size={16}
            className={`transition-transform duration-500 ${open ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="details"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
              data-testid={`details-${t.id}`}
            >
              <div className="pt-6 mt-6 border-t border-[#E8DED5]">
                {t.details.kind === "items" && (
                  <div className="space-y-6">
                    {t.details.items.map((sub) => (
                      <div
                        key={sub.id}
                        className="flex gap-5 items-start"
                        data-testid={`subtreatment-${sub.id}`}
                      >
                        <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-[#F3EBE3]">
                          <img
                            src={sub.image}
                            alt={sub.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-serif text-[20px] text-[#2D241E] leading-tight mb-2">
                            {sub.title}
                          </h4>
                          <p className="body-text text-[14px] leading-relaxed">
                            {sub.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {t.details.kind === "text" && (
                  <div>
                    {t.details.body.map((p, i) => (
                      <p
                        key={i}
                        className="body-text text-[14px] leading-relaxed mb-4"
                      >
                        {p}
                      </p>
                    ))}
                    {t.details.indications && (
                      <div className="mt-5 pt-5 border-t border-[#E8DED5]">
                        <div className="text-xs uppercase tracking-widest text-[#BE6C42] font-semibold mb-3">
                          Indications
                        </div>
                        <ul className="space-y-2">
                          {t.details.indications.map((ind) => (
                            <li
                              key={ind}
                              className="flex items-start gap-3 text-[#2D241E] text-[14px]"
                            >
                              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#BE6C42] shrink-0" />
                              <span>{ind}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
};

export const Treatments = () => {
  return (
    <section
      id="traitements"
      data-testid="treatments-section"
      className="py-20 md:py-32 relative"
    >
      <div className="container-xl">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="overline">Nos Traitements</span>
            <h2 className="h2 text-4xl sm:text-5xl lg:text-6xl mt-5">
              Des soins complets,
              <br />
              <span className="italic text-[#BE6C42] font-light">
                pour chaque besoin.
              </span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 lg:pt-6"
          >
            <p className="body-text text-base sm:text-lg">
              Du soin préventif à l&apos;esthétique dentaire, nous
              accompagnons chaque patient avec des techniques modernes et un
              souci permanent du détail.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-start">
          {treatments.map((t, idx) => (
            <TreatmentCard key={t.id} t={t} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Treatments;
